import Vue from 'vue'
import Vuex from 'vuex'
import configFile from '@/../app-config'
import createPersistedState from 'vuex-persistedstate'
import addSeconds from 'date-fns/add_seconds'

import user from './modules/user'
import accounts from './modules/accounts'
import contracts from './modules/contracts'
import loans from './modules/loans'
import realestates from './modules/realestates'
import contractsCards from './modules/contracts-cards'
import series from './modules/series'
import funds from './modules/funds'
import banks from './modules/banks'
import investments from './modules/investments'
import error from './modules/error'
import toasts from './modules/toasts'
import notifications from './modules/notifications'
import advisor from './modules/advisor'
import customers from './modules/customers'
import theme from './modules/theme'
import reflect from '../utils/reflect-promise'
import { AppHolder } from '@/utils/app-holder'

Vue.use(Vuex)

function initialize () {
  store.registerModule('accounts', accounts())
  store.registerModule('contracts', contracts())
  store.registerModule('loans', loans())
  store.registerModule('realestates', realestates())
  store.registerModule('contracts-cards', contractsCards())
  store.registerModule('series', series())
  store.registerModule('funds', funds())
  store.registerModule('banks', banks())
  store.registerModule('investments', investments())
  store.registerModule('error', error())
  store.registerModule('toasts', toasts())
  store.registerModule('notifications', notifications())
  store.registerModule('customers', customers())
}

function reset () {
  store.unregisterModule('accounts')
  store.unregisterModule('contracts')
  store.unregisterModule('loans')
  store.unregisterModule('realestates')
  store.unregisterModule('contracts-cards')
  store.unregisterModule('series')
  store.unregisterModule('funds')
  store.unregisterModule('banks')
  store.unregisterModule('investments')
  store.unregisterModule('error')
  store.unregisterModule('toasts')
  store.unregisterModule('notifications')
  store.unregisterModule('customers')

  initialize()
}

const store = new Vuex.Store({
  namespaced: true,

  state: {
    notProd: process.env.NODE_ENV !== 'production',
    brandname: configFile[process.env.APP_ID].params.appId,
    brandnameAdvisory: `${configFile[process.env.APP_ID].params.appId}-advisory`,
    authExpiresAt: null,
    patchnoteOpened: false,
    sidebarMode: null,
    privateMode: false,
    privateModeAdvisory: false,
    cookieAccepted: false,
    openedIntros: {},
    appCfg: configFile[process.env.APP_ID].params
  },

  actions: {
    fetchAuthenticated ({ dispatch }, cached = true) {
      return dispatch('user/fetch', cached)
        .then((user) => Promise.all([
          dispatch('accounts/fetch', cached),
          dispatch('realestates/fetch', cached),
          dispatch('series/fetch', cached),
          dispatch('notifications/fetch', { cached })
        ]).then(() => user))
    },

    fetchAdvisory ({ dispatch }, cached = true) {
      return dispatch('advisor/fetch', cached)
        .then(() => Promise.all([
          reflect(dispatch('customers/fetchStats')),
          reflect(dispatch('notifications/fetch', { cached }))
        ]))
        .catch(() => {
          dispatch('advisor/unauthenticate')
        })
    },

    fetchCustomerView ({ dispatch }, id, cached = true) {
      return Promise.all([
        dispatch('customers/fetchUser', {id, cached})
          .then(() => dispatch('accounts/fetch', false)),
        dispatch('series/fetch', cached)
      ])
    },

    reset,
    resetCustomer ({ commit }) {
      commit('accounts/reset')
      commit('contracts/reset')
      commit('loans/reset')
      commit('realestates/reset')
      commit('user/reset')
    }
  },

  modules: {
    user,
    advisor,
    theme
  },

  mutations: {
    updateToken (state, data) {
      state.authExpiresAt = addSeconds(new Date(), data)
    },
    patchnoteOpened (state, data) {
      state.patchnoteOpened = data
    },
    setSidebarMode (state, data) {
      state.sidebarMode = data
    },
    switchPrivateMode (state) {
      state.privateMode = !state.privateMode
    },
    switchPrivateModeAdvisory (state) {
      state.privateModeAdvisory = !state.privateModeAdvisory
    },
    acceptCookie (state) {
      state.cookieAccepted = true
    },
    setOpenedIntro (state, stepId) {
      state.openedIntros = {
        ...state.openedIntros,
        [stepId]: true
      }
    }
  },

  plugins: [
    createPersistedState({
      paths: [
        'user.authenticated',
        'authExpiresAt',
        'patchnoteOpened',
        'sidebarMode',
        'advisor.authenticated',
        'theme',
        'privateMode',
        'privateModeAdvisory',
        'cookieAccepted',
        'openedIntros'
      ]
      /* ,
      filter ({ type }) {
        return type in persistedMutations || type in persistedAdvisoryMutations
      } */
    })
  ]
})

initialize(store)

AppHolder.store = store

export default store
